import { useCallback } from "react";
import { useParams } from "react-router-dom";
import { languages } from "@/i18n";

const validLangCodes = new Set(languages.map((l) => l.code));

/**
 * Returns the current language from the URL param and a helper
 * to prefix any path with the current language.
 */
export function useLocalePath() {
  const { lang } = useParams<{ lang: string }>();
  const currentLang = lang && validLangCodes.has(lang) ? lang : "en";

  const localePath = useCallback(
    (path: string) => {
      // Already prefixed or external — return as-is
      if (path.startsWith("http") || path.startsWith("#")) return path;
      const clean = path.startsWith("/") ? path : `/${path}`;
      return `/${currentLang}${clean}`;
    },
    [currentLang],
  );

  return { lang: currentLang, localePath };
}

/**
 * Detects the best language from the browser's Accept-Language.
 */
export function detectBrowserLanguage(): string {
  const browserLangs = navigator.languages || [navigator.language];
  for (const bl of browserLangs) {
    const code = bl.split("-")[0].toLowerCase();
    if (validLangCodes.has(code)) return code;
  }
  return "en";
}
